<template>
  <div class="reaction-bar">
    <div class="reaction-button-wrapper">
      <button
        class="action-btn like-btn"
        :class="{ 'has-reacted': userHasReacted }"
        @click="toggleQuickReaction"
        @mouseenter="showQuickPicker = true"
        @mouseleave="delayHideQuickPicker"
      >
        <span v-if="userHasReacted" class="user-reaction">{{ userReaction }}</span>
        <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
        </svg>
        <span>{{ userHasReacted ? 'Reacted' : 'Like' }}</span>
      </button>

      <!-- Quick Reaction Picker (on hover) -->
      <div
        v-show="showQuickPicker"
        class="quick-picker"
        @mouseenter="cancelHideTimeout"
        @mouseleave="showQuickPicker = false"
      >
        <button
          v-for="emoji in quickReactions"
          :key="emoji"
          class="quick-emoji"
          :class="{ selected: userReaction === emoji }"
          @click.stop="toggleReaction(emoji)"
        >
          {{ emoji }}
        </button>
        <button class="more-emoji" @click.stop="openFullPicker">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#65676b">
            <circle cx="5" cy="12" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="19" cy="12" r="2"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Full Emoji Picker Modal -->
    <div v-if="showFullPicker" class="picker-overlay" @click="closeFullPicker">
      <div class="picker-container" @click.stop>
        <EmojiPicker
          :native="true"
          :hide-search="false"
          :hide-group-icons="false"
          @select="onEmojiSelect"
        />
      </div>
    </div>
  </div>
</template>

<script>
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";

export default {
  name: "ReactionBar",
  components: {
    EmojiPicker,
  },
  props: {
    reactions: {
      type: Object,
      default: () => ({}),
    },
    userId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ["toggle-reaction"],
  data() {
    return {
      showQuickPicker: false,
      showFullPicker: false,
      quickReactions: ["👍", "❤️", "😂", "😮", "😢", "🔥"],
      hideTimeout: null,
    };
  },
  computed: {
    userHasReacted() {
      return !!this.userReaction;
    },
    userReaction() {
      const userId = String(this.userId);
      for (const [emoji, users] of Object.entries(this.reactions)) {
        if (Array.isArray(users) && users.map(String).includes(userId)) {
          return emoji;
        }
      }
      return null;
    },
  },
  methods: {
    toggleQuickReaction() {
      if (this.userHasReacted) {
        // Remove current reaction
        this.$emit("toggle-reaction", this.userReaction);
      } else {
        // Add default like
        this.$emit("toggle-reaction", "👍");
      }
    },

    toggleReaction(emoji) {
      this.$emit("toggle-reaction", emoji);
      this.showQuickPicker = false;
    },

    delayHideQuickPicker() {
      this.hideTimeout = setTimeout(() => {
        this.showQuickPicker = false;
      }, 500);
    },

    cancelHideTimeout() {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      this.showQuickPicker = true;
    },

    openFullPicker() {
      this.showQuickPicker = false;
      this.showFullPicker = true;
    },

    closeFullPicker() {
      this.showFullPicker = false;
    },

    onEmojiSelect(emoji) {
      this.$emit("toggle-reaction", emoji.i);
      this.showFullPicker = false;
    },
  },
  beforeUnmount() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  },
};
</script>

<style scoped>
.reaction-bar {
  flex: 1;
  position: relative;
}

.reaction-button-wrapper {
  position: relative;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: none;
  background: none;
  color: #65676b;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.action-btn:hover {
  background: #f0f2f5;
}

.action-btn.has-reacted {
  color: #1877f2;
}

.user-reaction {
  font-size: 20px;
}

.quick-picker {
  position: absolute;
  bottom: calc(100% - 10px);
  left: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px 20px 14px;
  background: #fff;
  border-radius: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.quick-emoji {
  width: 44px;
  height: 44px;
  border: none;
  background: #f0f2f5;
  font-size: 26px;
  cursor: pointer;
  border-radius: 50%;
  transition: transform 0.15s ease, background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-emoji:hover {
  transform: scale(1.2);
  background: #e4e6eb;
}

.quick-emoji.selected {
  background: #d8dadf;
}

.more-emoji {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f2f5;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.more-emoji:hover {
  background: #e4e6eb;
}

.picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.picker-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .quick-picker {
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 10px 16px 10px;
    gap: 4px;
  }

  .quick-emoji {
    width: 38px;
    height: 38px;
    font-size: 22px;
  }

  .more-emoji {
    width: 28px;
    height: 28px;
  }

  .action-btn span:not(.user-reaction) {
    display: none;
  }
}
</style>
